import { useCallback, useEffect, useMemo, useState } from 'react'

import { IToken } from '@optimex-xyz/trade-wallet-sdk'

import i18n from '@/i18n'
import { DepositStatus, DepositStatusFilterOption, DepositWalletFilterOption } from '@/types'
import { SELECTED_ALL } from '@/utils'

import { useWallet } from '../use-wallet'

export type TokenFilterOption = IToken & {
  label: string
  value: string
}

export type FilterVaultDeposit = {
  token: TokenFilterOption
  address: DepositWalletFilterOption
  status: DepositStatusFilterOption
  pagination: {
    page: number
    pageSize: number
  }
}

export const DEFAULT_TOKEN_FILTER = {
  label: 'All Token',
  value: SELECTED_ALL,
  tokenLogoUri: '',
  tokenAddress: '',
  networkId: '',
} as TokenFilterOption

export const DEFAULT_ADDRESS_FILTER: DepositWalletFilterOption = {
  label: i18n.t('title.ALL_WALLETS'),
  value: SELECTED_ALL,
}

export const DEFAULT_PAGINATION = {
  page: 1,
  pageSize: 50,
}

enum FILTER_STATUS_VALUES {
  ALL = 'ALL',
  WAITING_CONFIRMATION = 'WAITING_CONFIRMATION',
  READY_TO_SWAP = 'READY_TO_SWAP',
  SWAPPED = 'SWAPPED',
  VAULT_CLOSED = 'VAULT_CLOSED',
}
export const DEFAULT_STATUS_FILTER = [
  {
    label: i18n.t('title.ALL_STATUS'),
    value: FILTER_STATUS_VALUES.ALL,
  },
  {
    label: i18n.t('vaultStatus.WAITING_CONFIRMATION'),
    value: FILTER_STATUS_VALUES.WAITING_CONFIRMATION,
  },
  {
    label: i18n.t('vaultStatus.READY_TO_SWAP'),
    value: FILTER_STATUS_VALUES.READY_TO_SWAP,
  },
  {
    label: i18n.t('vaultStatus.SWAPPED'),
    value: FILTER_STATUS_VALUES.SWAPPED,
  },
  {
    label: i18n.t('vaultStatus.VAULT_CLOSED'),
    value: FILTER_STATUS_VALUES.VAULT_CLOSED,
  },
]

const STATUS_MAP: Record<FILTER_STATUS_VALUES, DepositStatus[]> = {
  [FILTER_STATUS_VALUES.ALL]: [],
  [FILTER_STATUS_VALUES.WAITING_CONFIRMATION]: [DepositStatus.WAITING_CONFIRMATION],
  [FILTER_STATUS_VALUES.READY_TO_SWAP]: [DepositStatus.DEPOSITED, DepositStatus.SWAP_FAILED],
  [FILTER_STATUS_VALUES.SWAPPED]: [DepositStatus.PROCESSING_SWAP, DepositStatus.SWAP_COMPLETED],
  [FILTER_STATUS_VALUES.VAULT_CLOSED]: [
    DepositStatus.PRE_REFUND,
    DepositStatus.REFUNDED,
    DepositStatus.DEPOSIT_FAILED,
    DepositStatus.TIMEOUT,
  ],
}

const DEFAULT_FILTER: FilterVaultDeposit = {
  token: DEFAULT_TOKEN_FILTER,
  address: DEFAULT_ADDRESS_FILTER,
  status: DEFAULT_STATUS_FILTER[0],
  pagination: DEFAULT_PAGINATION,
}

export const useFilterVaultDeposit = () => {
  const [filter, setFilter] = useState<FilterVaultDeposit>(DEFAULT_FILTER)
  const { walletAddresses, isConnected } = useWallet()

  const handleTokenChange = useCallback((value: TokenFilterOption) => {
    setFilter((prev) => ({
      ...prev,
      token: value,
      pagination: DEFAULT_PAGINATION,
    }))
  }, [])

  const handleAddressChange = useCallback((value: DepositWalletFilterOption) => {
    setFilter((prev) => ({
      ...prev,
      address: value,
      pagination: DEFAULT_PAGINATION,
    }))
  }, [])

  const handleStatusChange = useCallback((value: DepositStatusFilterOption) => {
    setFilter((prev) => ({
      ...prev,
      status: value,
      pagination: DEFAULT_PAGINATION,
    }))
  }, [])

  const getStatusValues = useCallback((statusValue: string): DepositStatus[] => {
    return STATUS_MAP[statusValue as FILTER_STATUS_VALUES] || []
  }, [])

  const handlePageChange = useCallback((page: number) => {
    setFilter((prev) => ({
      ...prev,
      pagination: {
        ...prev.pagination,
        page,
      },
    }))
  }, [])

  const handlePageSizeChange = useCallback((pageSize: number) => {
    setFilter((prev) => ({
      ...prev,
      pagination: {
        ...prev.pagination,
        pageSize,
        page: 1, // Reset to first page when changing page size
      },
    }))
  }, [])

  const resetFilters = useCallback(() => {
    setFilter(DEFAULT_FILTER)
  }, [])

  const fromAddress = useMemo(() => {
    if (filter.address.value === SELECTED_ALL) return walletAddresses
    const currentWallet = walletAddresses.filter((wl) => wl === filter.address.value)
    return currentWallet
  }, [walletAddresses, filter.address.value])

  // Reset filters when wallet is disconnected
  useEffect(() => {
    if (isConnected) return
    resetFilters()
  }, [isConnected, resetFilters])

  // Reset to all wallets if the selected wallet is disconnected
  useEffect(() => {
    if (!isConnected || filter.address.value === SELECTED_ALL || walletAddresses.includes(filter.address.value)) return
    handleAddressChange(DEFAULT_ADDRESS_FILTER)
  }, [walletAddresses, isConnected, filter.address.value])

  return {
    filter,
    handleTokenChange,
    handleAddressChange,
    handleStatusChange,
    handlePageChange,
    handlePageSizeChange,
    resetFilters,
    fromAddress,
    getStatusValues,
  }
}
